/**
 * Helpers para trabajar con fechas en formato ISO corto `YYYY-MM-DD`.
 *
 * Este es el formato que emite el `DatePicker` del DS y el que viaja
 * en el state del wizard. Para serializar al backend cuando se necesita
 * ISO UTC completo, usar `toIsoUtc`.
 *
 * El orden lexicográfico de strings `YYYY-MM-DD` coincide con el orden
 * cronológico, así que comparaciones `<`, `>`, `===` funcionan sin
 * convertir a `Date` (y se evita el drift por zona horaria).
 */

/** Regex de fecha ISO corta `YYYY-MM-DD`. Única fuente del proyecto. */
export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Type guard. Devuelve `true` si el valor es un string `YYYY-MM-DD`. */
export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE_RE.test(value);
}

/**
 * Fecha de hoy en formato ISO `YYYY-MM-DD` en zona horaria local.
 * Útil como cota inferior para DatePickers que no deben aceptar
 * fechas pasadas.
 */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Máximo ISO date (orden lexicográfico ≡ cronológico) ignorando
 * valores no-ISO, `null` y `undefined`. Devuelve `undefined` si
 * ninguno de los inputs es una fecha válida.
 */
export function maxIso(...values: ReadonlyArray<string | undefined | null>): string | undefined {
  const valids = values.filter(isIsoDate);
  if (valids.length === 0) return undefined;
  return valids.reduce((a, b) => (a > b ? a : b));
}

/** Mínimo ISO date (orden lexicográfico). Mismo contrato que `maxIso`. */
export function minIso(...values: ReadonlyArray<string | undefined | null>): string | undefined {
  const valids = values.filter(isIsoDate);
  if (valids.length === 0) return undefined;
  return valids.reduce((a, b) => (a < b ? a : b));
}

/**
 * Convierte `YYYY-MM-DD` a ISO UTC completo `YYYY-MM-DDT00:00:00.000Z`.
 * Si el string ya trae hora (`T...`) lo deja pasar sin tocar. Útil
 * para serializar al backend cuando el contrato exige ISO completo.
 */
export function toIsoUtc(value: string): string {
  return ISO_DATE_RE.test(value) ? `${value}T00:00:00.000Z` : value;
}

/**
 * Inversa de `toIsoUtc`: extrae la parte `YYYY-MM-DD` de un ISO date o
 * date-time. El backend persiste las fechas de día como UTC midnight
 * (`...T00:00:00.000Z`), así que cortar los primeros 10 caracteres recupera
 * la fecha de calendario sin drift por zona horaria. Útil para alimentar el
 * `DatePicker` (que trabaja en `YYYY-MM-DD`) con fechas que llegan del
 * backend en formato date-time. Devuelve `''` si no empieza por una fecha
 * ISO válida.
 */
export function toIsoDay(value: string | null | undefined): string {
  if (!value) return '';
  const day = value.slice(0, 10);
  return ISO_DATE_RE.test(day) ? day : '';
}

/**
 * Día calendario **local** (`YYYY-MM-DD`) de una fecha-hora ISO. A diferencia de
 * `toIsoDay` —que corta el día UTC asumiendo almacenamiento en UTC midnight—,
 * respeta la zona del usuario. Úsese para fechas que el backend guarda como
 * **fin de día local** (p. ej. `fechaLimite`/prórroga, vía `fechaAIsoLimite`):
 * en zonas con offset negativo esas caen en el día UTC siguiente, así que
 * cortar el UTC daría un día de más. Coincide con lo que muestra `formatDate`.
 * Devuelve `''` si el valor no es una fecha válida.
 */
export function toLocalIsoDay(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Suma `days` días a una fecha ISO `YYYY-MM-DD` y devuelve la nueva
 * en el mismo formato. Si `value` no es ISO válido devuelve `value`
 * sin tocar. Usa la zona local — el formato es solo fecha y los
 * cómputos de día completo no introducen drift.
 */
export function addDaysIso(value: string, days: number): string {
  if (!ISO_DATE_RE.test(value)) return value;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const next = new Date(y, m - 1, d + days);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(
    next.getDate(),
  ).padStart(2, '0')}`;
}
